import { FC, useEffect, useMemo, useState, ReactNode } from "react";
import type { DeepPartial } from "@ortex-charts/core";
import type { SankeyChartOptions } from "@ortex-charts/viz";
import { SankeyView } from "@/components/ortex-charts/Viz";
import { Box, IconButton, MenuItem, Select, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useAtomValue } from "jotai";
import { greyState, negativeState, positiveState, textColorState, themeState } from "@/lib/store";
import { Stock } from "@/models/stock";
import { FlowPeriod, FundamentalsFlow as FundamentalsFlowType, FlowSegment } from "@/models/fundamentals-flow";
import { getFundamentalsFlow } from "@/controllers/fundamentals-flow";
import { nFormatter } from "@/lib/utils";
import Module from "@/components/module/Module";
import Skeleton from "@/components/skeleton/Skeleton";
import { brighten } from "@/lib/ortex-charts/color";

const MAX_SEGMENTS = 7;
const SEGMENT_COLORS = ["#3D7DD9", "#5A93E0", "#77A9E7", "#94BFEE", "#B1D5F5", "#8E7CC3", "#A98FD1", "#C4A2DF"];
const TAX_BENEFIT_COLOR = "#D08A2E";

interface PropsTypes {
    children?: ReactNode;
    stock: Stock;
    range: string;
    helpModuleId?: string;
    onStartTour?: () => void;
}

interface FlowNode {
    id: string;
    color: string;
    column?: number;
}

interface FlowLink {
    from: string;
    to: string;
    weight: number;
}

const positive = (value: number | undefined | null): number => (typeof value === "number" && value > 0 ? value : 0);

/** Build the sankey topology for one fiscal period.
 *
 * Profitable periods flow left to right: segments → revenue → gross profit
 * → operating income → pre-tax income → net income, with expenses branching
 * off in red at each stage. Loss-making periods keep every number on the
 * chart by adding red funding sources — the net loss (and a tax benefit,
 * when there is one) feed a pre-tax loss node that covers the expense
 * overruns, the way the classic income-statement sankeys draw it.
 */
const buildFlow = (period: FlowPeriod, segments: FlowSegment[], positiveColor: string, negativeColor: string, revenueColor: string) => {
    const inc = period.income;
    const revenue = positive(inc.revenue);
    const grossProfit = inc.grossProfit ?? (inc.revenue != null && inc.costOfRevenue != null ? inc.revenue - inc.costOfRevenue : undefined);
    const costOfRevenue = inc.costOfRevenue ?? (inc.revenue != null && grossProfit != null ? inc.revenue - grossProfit : undefined);
    const operatingIncome = inc.operatingIncome;
    const pretax = inc.pretaxIncome ?? (inc.netIncome != null && inc.tax != null ? inc.netIncome + inc.tax : undefined);
    const tax = inc.tax ?? (pretax != null && inc.netIncome != null ? pretax - inc.netIncome : undefined);
    const netIncome = inc.netIncome;
    const epsilon = revenue * 0.004;

    const profit = (shade: number) => brighten(positiveColor, shade);
    const expense = (shade: number) => brighten(negativeColor, shade);

    const nodes: FlowNode[] = [];
    const links: FlowLink[] = [];
    const values: Record<string, number> = {};

    const addLink = (from: string, to: string, weight: number | undefined | null) => {
        if (typeof weight !== "number" || weight <= 0) return;
        links.push({ from, to, weight });
    };
    const addNode = (id: string, color: string, column: number | undefined, value: number | undefined | null) => {
        if (nodes.some((node) => node.id === id)) return;
        nodes.push({ id, color, column });
        if (typeof value === "number") values[id] = value;
    };

    // Segment layer (column 0) — positive-revenue segments feed Revenue.
    // Column indices must start at 0 or the sankey layout collapses, so every
    // downstream column shifts left when there is no segment layer.
    const positiveSegments = segments.filter((segment) => positive(segment.revenue) > 0);
    const hasSegmentLayer = revenue > 0 && positiveSegments.length >= 2;
    const col = (index: number) => (hasSegmentLayer ? index : index - 1);
    let eliminations = 0;
    if (hasSegmentLayer) {
        // Segments flow in at their reported (gross) values. Many companies
        // report segment revenue before intersegment eliminations, so the
        // inflow can exceed total revenue — the excess leaves Revenue again
        // as a red "Intersegment eliminations" outflow rather than silently
        // truncating the later segments.
        const top = positiveSegments.slice(0, MAX_SEGMENTS);
        let inflow = 0;
        top.forEach((segment, index) => {
            const weight = positive(segment.revenue);
            if (weight <= 0) return;
            inflow += weight;
            addNode(segment.name, SEGMENT_COLORS[index % SEGMENT_COLORS.length], 0, segment.revenue);
            addLink(segment.name, "Revenue", weight);
        });
        const rest = revenue - inflow;
        if (rest > revenue * 0.005) {
            addNode("Other segments", SEGMENT_COLORS[top.length % SEGMENT_COLORS.length], 0, rest);
            addLink("Other segments", "Revenue", rest);
        } else if (rest < -revenue * 0.005) {
            eliminations = -rest;
        }
    }

    addNode("Revenue", revenueColor, col(1), inc.revenue);
    if (eliminations > 0) {
        addNode("Intersegment eliminations", expense(0.3), col(2), -eliminations);
        addLink("Revenue", "Intersegment eliminations", eliminations);
    }

    // Stage A — revenue splits into cost of revenue and gross profit.
    const grossPositive = positive(grossProfit);
    if (grossPositive > 0) addNode("Gross profit", profit(0), col(2), grossProfit);
    if (costOfRevenue != null && costOfRevenue > 0) {
        addNode("Cost of revenue", expense(0), col(2), costOfRevenue);
        addLink("Revenue", "Cost of revenue", Math.min(costOfRevenue, revenue || costOfRevenue));
    }
    if (grossPositive > 0) addLink("Revenue", "Gross profit", grossPositive);

    // Stage B — operating expenses, fed by gross profit and (in loss years)
    // by the pre-tax loss. Feeders split proportionally across components so
    // every node conserves flow.
    const opexTotal = grossProfit != null && operatingIncome != null ? grossProfit - operatingIncome : undefined;
    const operatingDeficit = operatingIncome != null && operatingIncome < 0 ? -operatingIncome : 0;
    if (opexTotal != null && opexTotal > epsilon) {
        const rnd = positive(inc.rnd);
        const sga = positive(inc.sga);
        const otherOpex = Math.max(0, opexTotal - rnd - sga);
        const components: [string, number, string][] = [];
        if (rnd > epsilon) components.push(["R&D", rnd, expense(0.05)]);
        if (sga > epsilon) components.push(["SG&A", sga, expense(0.1)]);
        if (otherOpex > epsilon) components.push(["Other opex", otherOpex, expense(0.15)]);
        const componentSum = components.reduce((total, [, value]) => total + value, 0);
        const feeders: [string, number][] = [];
        if (grossPositive > 0) feeders.push(["Gross profit", Math.min(grossPositive, opexTotal)]);
        if (operatingDeficit > epsilon && pretax != null && pretax < 0) feeders.push(["Pre-tax loss", operatingDeficit]);
        components.forEach(([id, value, color]) => {
            addNode(id, color, col(3), value);
            feeders.forEach(([from, amount]) => addLink(from, id, componentSum > 0 ? (amount * value) / componentSum : 0));
        });
    }
    if (operatingIncome != null && operatingIncome > 0) {
        addNode("Operating income", profit(0.05), col(3), operatingIncome);
        addLink("Gross profit", "Operating income", Math.min(operatingIncome, grossPositive || operatingIncome));
    }

    // Stage C — below the operating line.
    const nonOperating = pretax != null && operatingIncome != null ? pretax - operatingIncome : undefined;
    if (pretax != null && pretax > 0) {
        addNode("Pre-tax income", profit(0.1), col(4), pretax);
        if (operatingIncome != null && operatingIncome > 0) addLink("Operating income", "Pre-tax income", Math.min(operatingIncome, pretax));
        if (nonOperating != null && nonOperating > epsilon) {
            addNode("Other income", profit(0.2), col(3), nonOperating);
            addLink("Other income", "Pre-tax income", nonOperating);
        } else if (nonOperating != null && nonOperating < -epsilon && operatingIncome != null && operatingIncome > 0) {
            addNode("Interest & other", expense(0.2), col(4), -nonOperating);
            addLink("Operating income", "Interest & other", -nonOperating);
        }
        if (tax != null && tax < -epsilon && netIncome != null && netIncome > 0) {
            // Profitable, with a tax benefit: net income exceeds pre-tax.
            addNode("Tax benefit", TAX_BENEFIT_COLOR, col(4), -tax);
            addNode("Net income", profit(0.15), col(5), netIncome);
            addLink("Pre-tax income", "Net income", pretax);
            addLink("Tax benefit", "Net income", -tax);
        } else {
            if (netIncome != null && netIncome > 0) {
                addNode("Net income", profit(0.15), col(5), netIncome);
                addLink("Pre-tax income", "Net income", Math.min(netIncome, pretax));
            }
            if (tax != null && tax > epsilon) {
                addNode("Tax", expense(0.25), col(5), tax);
                addLink("Pre-tax income", "Tax", Math.min(tax, pretax));
            }
        }
    } else if (pretax != null && pretax < 0) {
        // Loss year: the deficit is funded from the left — net loss plus any
        // tax benefit flow into a pre-tax loss node, which covers the
        // operating shortfall and the non-operating charges.
        const deficit = -pretax;
        addNode("Pre-tax loss", expense(0.05), col(2), deficit);
        if (netIncome != null && netIncome < 0) {
            addNode("Net loss", expense(-0.05), col(1), -netIncome);
            if (tax != null && tax < -epsilon) {
                addNode("Tax benefit", TAX_BENEFIT_COLOR, col(1), -tax);
                addLink("Net loss", "Pre-tax loss", -netIncome);
                addLink("Tax benefit", "Pre-tax loss", -tax);
            } else if (tax != null && tax > epsilon) {
                addNode("Tax", expense(0.25), col(2), tax);
                addLink("Net loss", "Pre-tax loss", deficit);
                addLink("Net loss", "Tax", tax);
            } else {
                addLink("Net loss", "Pre-tax loss", deficit);
            }
        }
        if (nonOperating != null && nonOperating < -epsilon) {
            addNode("Interest & other expenses", expense(0.2), col(3), -nonOperating);
            addLink("Pre-tax loss", "Interest & other expenses", Math.min(-nonOperating, deficit));
            if (operatingIncome != null && operatingIncome > 0) addLink("Operating income", "Interest & other expenses", operatingIncome);
        }
        // The operating-deficit share of the pre-tax loss was already linked
        // into the opex components by Stage B's feeder split.
    }

    return { nodes, links, values };
};

const marginOf = (part: number | undefined | null, revenue: number | undefined | null): number | null => (typeof part === "number" && typeof revenue === "number" && revenue !== 0 ? (part / revenue) * 100 : null);

const FundamentalsFlow: FC<PropsTypes> = ({ stock, range, children, helpModuleId, onStartTour }) => {
    const theme = useAtomValue(themeState);
    const grey = useAtomValue(greyState);
    const textColor = useAtomValue(textColorState);
    const positiveColor = useAtomValue(positiveState);
    const negativeColor = useAtomValue(negativeState);

    const [flow, setFlow] = useState<FundamentalsFlowType | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [periodIndex, setPeriodIndex] = useState<number>(0);
    const [segmentType, setSegmentType] = useState<"business" | "geographic" | "none">("business");

    useEffect(() => {
        setLoading(true);
        getFundamentalsFlow(stock.stock_id, range === "q" ? "q" : "a")
            .then((data) => {
                setFlow(data);
                setPeriodIndex(Math.max(0, data.periods.length - 1));
            })
            .catch(() => setFlow(null))
            .finally(() => setLoading(false));
    }, [stock.stock_id, range]);

    const periods = flow?.periods || [];
    const period = periods[periodIndex];
    const previousPeriod = periods[periodIndex - 1];

    const hasBusiness = periods.some((p) => p.businessSegments.filter((s) => positive(s.revenue) > 0).length >= 2);
    const hasGeographic = periods.some((p) => p.geographicSegments.filter((s) => positive(s.revenue) > 0).length >= 2);

    useEffect(() => {
        if (segmentType === "business" && !hasBusiness && hasGeographic) setSegmentType("geographic");
    }, [hasBusiness, hasGeographic]);

    const revenueColor = theme === "dark" ? "#3D7DD9" : "#2F6BC4";

    const sankey = useMemo<DeepPartial<SankeyChartOptions> | null>(() => {
        if (!period) return null;
        const segments = segmentType === "business" ? period.businessSegments : segmentType === "geographic" ? period.geographicSegments : [];
        const { nodes, links, values } = buildFlow(period, segments, positiveColor, negativeColor, revenueColor);
        const revenue = positive(period.income.revenue);
        const currency = flow?.currency || "";
        return {
            nodes: nodes.map((node) => ({ id: node.id, color: node.color, column: node.column })),
            links: links.map((link) => ({ from: link.from, to: link.to, value: link.weight })),
            nodeWidth: 16,
            nodePadding: 20,
            curve: 0.4,
            linkOpacity: theme === "dark" ? 0.5 : 0.4,
            linkColor: "gradient",
            format: { type: "custom", formatter: (value) => `${currency} ${nFormatter(value, 2)}` },
            // The node's own figure (a net income can be smaller than its throughput) and its share of revenue.
            labelFormat: (node, throughput) => {
                const value = values[node.id] ?? throughput;
                const share = revenue > 0 && node.id !== "Revenue" ? ` · ${((Math.abs(value) / revenue) * 100).toFixed(0)}%` : "";
                return [node.id, `${nFormatter(value, 2)}${share}`];
            },
        };
    }, [period, segmentType, theme, positiveColor, negativeColor, flow?.currency, revenueColor]);

    const badges = useMemo(() => {
        if (!period) return [];
        const inc = period.income;
        const prevInc = previousPeriod?.income;
        const items: { label: string; value: string; delta?: string; negative?: boolean }[] = [];
        if (typeof inc.revenue === "number") {
            const growth = prevInc?.revenue ? ((inc.revenue - prevInc.revenue) / Math.abs(prevInc.revenue)) * 100 : null;
            items.push({
                label: "Revenue",
                value: `${flow?.currency || ""} ${nFormatter(inc.revenue, 2)}`,
                delta: growth != null ? `${growth >= 0 ? "▲" : "▼"} ${Math.abs(growth).toFixed(1)}%` : undefined,
                negative: growth != null && growth < 0,
            });
        }
        const margins: [string, number | undefined][] = [
            ["Gross margin", inc.grossProfit],
            ["Op. margin", inc.operatingIncome],
            ["Net margin", inc.netIncome],
        ];
        margins.forEach(([label, part]) => {
            const margin = marginOf(part, inc.revenue);
            if (margin != null) items.push({ label, value: `${margin.toFixed(1)}%`, negative: margin < 0 });
        });
        if (typeof inc.netIncome === "number" && inc.netIncome < 0) {
            items.push({ label: "Net loss", value: `${flow?.currency || ""} ${nFormatter(inc.netIncome, 2)}`, negative: true });
        }
        return items;
    }, [period, previousPeriod, flow?.currency]);

    return (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <div>{children}</div>
            <Module title="Income Statement Flow" help={{ moduleId: helpModuleId || "fundamentals", modulePage: "V2_stock_fundamentals" }} helpActions={onStartTour ? [{ label: "Restart the guided tour", onClick: onStartTour }] : undefined}>
                {loading ? (
                    <Skeleton variant="rounded" width="100%" height="100%" loading />
                ) : !period || !sankey ? (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                        <Typography color={textColor}>No flow data available for this company</Typography>
                    </Box>
                ) : (
                    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", px: 2, pt: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }} data-tour="fundamentals-flow-timeline">
                            <IconButton size="small" onClick={() => setPeriodIndex(Math.max(0, periodIndex - 1))} sx={{ color: textColor }} disabled={periodIndex === 0} aria-label="previous period">
                                <ChevronLeftIcon />
                            </IconButton>
                            <Select
                                size="small"
                                value={periodIndex}
                                inputProps={{ "aria-label": "Period" }}
                                onChange={(event) => setPeriodIndex(Number(event.target.value))}
                                sx={{ minWidth: 110, fontWeight: 600 }}
                                MenuProps={{ slotProps: { paper: { sx: { maxHeight: 320 } } } }}
                            >
                                {periods
                                    .map((entry, index) => ({ entry, index }))
                                    .reverse()
                                    .map(({ entry, index }) => (
                                        <MenuItem key={entry.label} value={index}>
                                            {entry.label}
                                        </MenuItem>
                                    ))}
                            </Select>
                            <IconButton size="small" onClick={() => setPeriodIndex(Math.min(periods.length - 1, periodIndex + 1))} sx={{ color: textColor }} disabled={periodIndex >= periods.length - 1} aria-label="next period">
                                <ChevronRightIcon />
                            </IconButton>
                            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", flex: 1 }}>
                                {badges.map((badge) => (
                                    <Box key={badge.label} sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
                                        <Typography variant="caption" sx={{ color: grey }}>
                                            {badge.label}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: badge.negative ? negativeColor : textColor, fontWeight: 500 }}>
                                            {badge.value}
                                        </Typography>
                                        {badge.delta && (
                                            <Typography variant="caption" sx={{ color: badge.negative ? negativeColor : positiveColor }}>
                                                {badge.delta}
                                            </Typography>
                                        )}
                                    </Box>
                                ))}
                            </Box>
                            {(hasBusiness || hasGeographic) && (
                                <ToggleButtonGroup size="small" exclusive value={segmentType} onChange={(_, value) => value && setSegmentType(value)} data-tour="fundamentals-flow-segments">
                                    {hasBusiness && <ToggleButton value="business">Segments</ToggleButton>}
                                    {hasGeographic && <ToggleButton value="geographic">Regions</ToggleButton>}
                                    <ToggleButton value="none">Income only</ToggleButton>
                                </ToggleButtonGroup>
                            )}
                        </Box>
                        <Box sx={{ flex: 1, minHeight: 0, pb: 1 }} data-tour="fundamentals-flow">
                            <SankeyView options={sankey} />
                        </Box>
                    </Box>
                )}
            </Module>
        </Box>
    );
};

export default FundamentalsFlow;
